import { useEffect, useRef, useState } from 'react';
import useLocale from './localeHook';
import useDebounce from './debounceHook';
import { suggestions } from '../search';
import useFeatureFlags from './featureFlagsHook';
import { MAX_SUGGESTIONS_COUNT, SEARCH_WITH_ONE_CHAR } from '../../constants';

/**
 * Hook to handle the updating of the searchSuggestions when the searchTerm changes
 * or the search opens for the first time with a search term derived from a query param
 * @param {string} searchTerm
 * @param {boolean} isSearchOpen
 * @returns {searchSuggestions: object[]}
 */
export default function useSearchSuggestions(searchTerm, isSearchOpen) {
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const { countryOrMarketplaceId: country, language } = useLocale();
  const debouncedSearchTermRef = useRef(debouncedSearchTerm);
  const minimumSearchTermLength = useFeatureFlags(SEARCH_WITH_ONE_CHAR) ? 1 : 2;

  useEffect(() => {
    if (!searchTerm && searchSuggestions.length) {
      setSearchSuggestions([]);
    }
  }, [searchTerm, searchSuggestions]);

  useEffect(() => {
    if (!debouncedSearchTerm) {
      setSearchSuggestions([]);
      return;
    }

    if (!isSearchOpen) return;

    debouncedSearchTermRef.current = debouncedSearchTerm;
    // Delay fetching suggestions until searchTerm length is met.
    if (debouncedSearchTermRef.current.length < minimumSearchTermLength) return;

    (async () => {
      const { searchTerms = [] } = await suggestions(debouncedSearchTerm, { country, language });

      // This fixes a timing issue where the search results do not match the current debounced term
      if (searchTerm && debouncedSearchTermRef.current === debouncedSearchTerm) {
        setSearchSuggestions(searchTerms.slice(0, MAX_SUGGESTIONS_COUNT));
      }
    })();
  }, [debouncedSearchTerm, isSearchOpen]);

  return searchSuggestions;
}
